// Section 12 and 22: the sick and injured in triage order, with what they got last season and
// what they will get next season, their death risk (untreated, and as planned) with its factor
// list, and the player's marks by name ("treat first" / "do not treat"). Props only.
import type { JobId, PersonId } from '../../sim/api';
import type { HealthPatientRow } from '../../viewModels/health';
import { percent } from '../format';
import { careLabel, healthText, illnessLabel } from '../text/en';
import { ExplainNumber } from './explain-number';

interface Props {
  rows: readonly HealthPatientRow[];
  manual: boolean;
  canEdit: boolean;
  jobLabel: (job: JobId) => string;
  onMark: (personId: PersonId, mark: boolean | null) => void;
}

function illnessOf(row: HealthPatientRow): string {
  if (row.illnessId === null) return illnessLabel.injury;
  const name = illnessLabel[row.illnessId];
  return row.injured ? `${name} ${healthText.injuredToo}` : name;
}

function MarkButtons({
  row,
  canEdit,
  onMark,
}: {
  row: HealthPatientRow;
  canEdit: boolean;
  onMark: Props['onMark'];
}) {
  return (
    <div class="health__marks" role="group" aria-label={`${healthText.mark}: ${row.name}`}>
      <button
        type="button"
        class={`button button--small${row.mark === true ? ' is-selected' : ''}`}
        aria-pressed={row.mark === true}
        disabled={!canEdit}
        onClick={() => onMark(row.id, row.mark === true ? null : true)}
      >
        {healthText.treatFirst}
      </button>
      <button
        type="button"
        class={`button button--small${row.mark === false ? ' is-selected' : ''}`}
        aria-pressed={row.mark === false}
        disabled={!canEdit}
        onClick={() => onMark(row.id, row.mark === false ? null : false)}
      >
        {healthText.doNotTreat}
      </button>
      {row.markPending && <span class="hint"> {healthText.markChanged}</span>}
    </div>
  );
}

export function PatientsTable({ rows, manual, canEdit, jobLabel, onMark }: Props) {
  return (
    <section class="health__section health__patients" aria-labelledby="health-patients">
      <h3 id="health-patients">
        {healthText.patients} <span class="num">({rows.length})</span>
      </h3>
      {rows.length === 0 ? (
        <p class="hint">{healthText.noPatients}</p>
      ) : (
        <table class={`table health__table${manual ? ' is-manual' : ''}`}>
          <thead>
            <tr>
              <th scope="col">{healthText.rank}</th>
              <th scope="col">{healthText.name}</th>
              <th scope="col">{healthText.illness}</th>
              <th scope="col">{healthText.seasonsLeft}</th>
              <th scope="col">{healthText.lastSeason}</th>
              <th scope="col">{healthText.nextSeason}</th>
              <th scope="col">{healthText.deathRisk}</th>
              <th scope="col">{healthText.mark}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} class={`health__row health__row--${row.planned}`}>
                <td class="num">{row.rank}</td>
                <th scope="row" class="health__name">
                  {row.name}{' '}
                  <span class="hint">
                    {row.ageYears}, {jobLabel(row.job).toLowerCase()}
                  </span>
                </th>
                <td>
                  {illnessOf(row)}
                  {row.contagious && <span class="health__tag">{healthText.contagious}</span>}
                  {row.quarantined && <span class="health__tag">{healthText.quarantined}</span>}
                  {row.treatedCourse && <span class="health__tag">{healthText.treatedCourse}</span>}
                </td>
                <td class="num">{row.seasonsRemaining}</td>
                <td>{row.lastCare === null ? '' : careLabel[row.lastCare]}</td>
                <td>
                  <span class={`health__care health__care--${row.planned}`}>
                    {careLabel[row.planned]}
                  </span>
                </td>
                <td>
                  <ExplainNumber
                    value={healthText.untreatedRisk(percent(row.deathChance))}
                    factors={row.deathFactors}
                    label={`${healthText.deathRisk}, ${row.name}`}
                  />
                  {row.plannedDeathChance !== row.deathChance && (
                    <span class="hint num">
                      {healthText.plannedRisk(percent(row.plannedDeathChance))}
                    </span>
                  )}
                </td>
                <td>
                  <MarkButtons row={row} canEdit={canEdit} onMark={onMark} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
